"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Eye,
  FileText,
  ChevronLeft,
  ChevronRight,
  Search,
  LayoutGrid,
  List,
  MoreVertical,
  Phone,
  Mail,
  Calendar,
  Clipboard,
  Users,
  ExternalLink,
  UserPlus,
  X,
  Edit2,
  Power,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Alert, Checkbox, message, Modal } from "antd";
import { axiosRDV } from "../../../../api/axios";
import {
  DEFAULT_ACCESS_SCOPES,
  MEDICAL_ACCESS_SCOPE_OPTIONS,
} from "../../../../authorization/useMedicalCapabilities";

import { fetchUsers } from "../../../../Profile/redux/thunks/userThunks";
import {
  createLocalPatient,
  updateLocalPatient,
  deactivateLocalPatient,
  reactivateLocalPatient,
  deleteLocalPatient,
} from "../../../../WaitingRoom/redux/thunks/localPatient.thunks";
import { selectAllLocalPatients, selectLocalPatientsLoading } from "../../../../WaitingRoom/redux/selectors/localPatient.selectors";
import { fetchLocalPatients } from "../../../../WaitingRoom/redux/thunks/localPatient.thunks";
import { fetchRendezvousByMedecin } from "../../../../appointments/redux/thunks/rendezvousThunks";
import ConfirmDeleteModal from "../../../../appointments/components/ConfirmDeleteModal/ConfirmDeleteModal";
import { DOCTOR_ROUTES } from "../../../../routes/patientRoutes/routePaths";
import { medicalAccessTargetKey } from "./patientIdentity";
import "./PatientsList.css";

/* ========================= Helpers ========================= */
const arr = (x) =>
  Array.isArray(x) ? x : Array.isArray(x?.data) ? x.data : Array.isArray(x?.items) ? x.items : [];

const norm = (v) => { const s = String(v ?? "").trim(); return s || null; };
const uniq = (xs) => Array.from(new Set((xs || []).map(norm).filter(Boolean)));

const safeJson = (v) => { try { return JSON.parse(v); } catch { return null; } };

const toNumericIfPossible = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  if (!/^\d+$/.test(s)) return s;
  const n = Number(s);
  return Number.isSafeInteger(n) ? n : s;
};

const pickNavigationPatientId = (p) => {
  const candidates = [p?.id, p?.userId, p?.patientId, p?.externalId, p?.uuid, p?._id]
    .map(toNumericIfPossible)
    .filter((x) => x !== null);
  const num = candidates.find((x) => typeof x === "number");
  if (num !== undefined) return num;
  return candidates[0] ?? "";
};

const getInitials = ({ firstName, lastName, fullName, email }) => {
  const name = fullName || `${firstName || ""} ${lastName || ""}`.trim() || email || "?";
  const first = firstName?.trim()?.[0] || name.split(" ")[0]?.[0] || "";
  const last  = lastName?.trim()?.[0]  || name.split(" ").slice(1).join(" ")?.[0] || "";
  return (first + last).toUpperCase() || "?";
};

const AVATAR_COLORS = ["#0f9688","#0d8076","#0a6b64","#12a899","#158a7d","#0e7a70"];
const avatarColor = (name = "") => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const getShortId = (id) => {
  if (!id) return null;
  const s = String(id).trim();
  const parts = s.split("-");
  return parts[0] || null;
};

const displayIdentifier = (value) => {
  const text = String(value ?? "").trim();
  return text || null;
};

const formatPatientPhone = (value) => {
  const phone = String(value ?? "").trim();
  if (!phone || phone.startsWith("+") || !/^\d[\d\s().-]*$/.test(phone)) return phone;
  return `+${phone}`;
};

const normalizeSource = (value) => String(value ?? "").trim().toLowerCase();
const isMedilinkSource = (value) => {
  const source = normalizeSource(value);
  return source === "medilink" || source === "subprofile";
};

const rowKey = (row) => `${normalizeSource(row?.source) || "unknown"}:${String(row?.patientId ?? "")}`;

const rowAccessTargetKey = (row) =>
  medicalAccessTargetKey(row?.principalPatientId ?? row?.patientId, row?.subprofileId);

const normalizeText = (value) => String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");

const buildLocalPatientKeySet = (patients) => {
  const keys = new Set();

  (patients || []).forEach((patient) => {
    if (!patient) return;
    [
      patient.id,
      patient._id,
      patient.patientId,
      patient.identifier,
      patient.patientIdentifier,
      patient.uuid,
      patient.externalId,
      patient.firstName,
      patient.lastName,
      patient.fullName,
      patient.name,
      patient.phone,
      patient.phoneNumber,
      patient.email,
    ]
      .filter(Boolean)
      .forEach((value) => {
        const text = String(value).trim();
        if (!text) return;
        keys.add(text);
        keys.add(normalizeText(text));
      });

    const combinedName = [patient.firstName, patient.lastName].filter(Boolean).join(" ").trim();
    if (combinedName) {
      keys.add(combinedName);
      keys.add(normalizeText(combinedName));
    }
  });

  return keys;
};

const getRowIdentityKeys = (patient) => {
  const keys = [
    patient?.id,
    patient?._id,
    patient?.patientId,
    patient?.identifier,
    patient?.patientIdentifier,
    patient?.uuid,
    patient?.externalId,
    patient?.phone,
    patient?.phoneNumber,
    patient?.email,
    patient?.firstName,
    patient?.lastName,
    patient?.fullName,
    patient?.name,
  ]
    .filter(Boolean)
    .flatMap((value) => {
      const text = String(value).trim();
      return text ? [text, normalizeText(text)] : [];
    });

  const combinedName = [patient?.firstName, patient?.lastName].filter(Boolean).join(" ").trim();
  if (combinedName) {
    keys.push(combinedName, normalizeText(combinedName));
  }

  return Array.from(new Set(keys.filter(Boolean)));
};

// Auto-calculate age from dateOfBirth (returns object with years, months, days)
const calculateAge = (dateString) => {
  if (!dateString) return null;
  const today = new Date();
  const birthDate = new Date(dateString);
  if (isNaN(birthDate.getTime())) return null;
  
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();
  
  // Adjust for negative days
  if (days < 0) {
    months--;
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
  }
  
  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }
  
  return { years, months, days };
};

const normalizeAge = (ageValue, dateOfBirth) => {
  if (dateOfBirth) {
    const calculated = calculateAge(dateOfBirth);
    if (calculated) return calculated;
  }
  if (ageValue && typeof ageValue === "object") return ageValue;
  if (typeof ageValue === "number" && Number.isFinite(ageValue)) {
    return { years: ageValue, months: 0, days: 0 };
  }
  return null;
};

// Format age for display: "56 ans" or "5 mois" or "15 jours"
const formatAge = (ageObj) => {
  if (!ageObj) return null;
  if (ageObj.years >= 1) {
    return ageObj.years;
  } else if (ageObj.months >= 1) {
    return ageObj.months; // Return months when < 1 year
  } else {
    return ageObj.days;   // Return days when < 1 month
  }
};

// Get age unit for display: "ans", "mois", "jours"
const getAgeUnit = (ageObj) => {
  if (!ageObj) return null;
  if (ageObj.years >= 1) {
    return "years"; // Will translate to "ans"
  } else if (ageObj.months >= 1) {
    return "months"; // Will translate to "mois"
  } else {
    return "days";   // Will translate to "jours"
  }
};

// Format age for display with i18n
const formatAgeDisplay = (ageObj, t) => {
  if (typeof ageObj === "number" && Number.isFinite(ageObj)) {
    ageObj = { years: ageObj, months: 0, days: 0 };
  }
  if (!ageObj) return "—";
  const formattedValue = formatAge(ageObj);
  const unit = getAgeUnit(ageObj);
  
  if (!unit) return "—";
  
  let translationKey = "card.years";
  if (unit === "months") translationKey = "card.months";
  if (unit === "days") translationKey = "card.days";
  
  const unitLabel = t(translationKey, { defaultValue: unit });
  return `${formattedValue} ${unitLabel}`;
};

/* ========================= Add Patient Modal ========================= */
function AddPatientModal({ onClose, onCreated, patient, onUpdated }) {
  const { t } = useTranslation("patientsList");
  const dispatch = useDispatch();
  const editing = Boolean(patient?.id);
  const [form, setForm] = useState(() => ({
    firstName: patient?.firstName || "",
    lastName: patient?.lastName || "",
    dateOfBirth: patient?.dateOfBirth || "",
    gender: patient?.gender || "Male",
    phone: patient?.phone || "",
    email: patient?.email || "",
    notes: patient?.notes || "",
  }));
  const [submitting, setSubmitting] = useState(false);
  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));
  
  const canSubmit = form.firstName.trim() && form.lastName.trim() && form.phone.trim() && form.dateOfBirth;

  const GENDERS = [
    { value: "Male",   label: t("modal.male")   },
    { value: "Female", label: t("modal.female") },
  ];

  const calculatedAge = calculateAge(form.dateOfBirth);

  async function handleSubmit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      // Convert date to ISO 8601 format (YYYY-MM-DD)
      const payload = {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          dateOfBirth: form.dateOfBirth,
          gender: form.gender,
          phone: form.phone.trim(),
          email: form.email.trim() || null,
          notes: form.notes.trim() || null,
      };
      const result = await dispatch(
        editing
          ? updateLocalPatient({ patientId: patient.id, ...payload })
          : createLocalPatient(payload)
      ).unwrap();
      editing ? onUpdated?.(result) : onCreated?.(result);
      onClose();
    } catch {
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="pm__modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pm__modal" role="dialog" aria-modal="true">
        <div className="pm__modal-header">
          <div>
            <h2 className="pm__modal-title">{editing ? "Edit Local Patient" : t("modal.title")}</h2>
            <p className="pm__modal-subtitle">{t("modal.subtitle")}</p>
          </div>
          <button className="pm__modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="pm__form-row">
          <div className="pm__form-group">
            <label className="pm__form-label">{t("modal.firstName")} <span className="pm__required">*</span></label>
            <input className="pm__form-input" placeholder={t("modal.firstNamePlaceholder")} value={form.firstName} onChange={set("firstName")} />
          </div>
          <div className="pm__form-group">
            <label className="pm__form-label">{t("modal.lastName")} <span className="pm__required">*</span></label>
            <input className="pm__form-input" placeholder={t("modal.lastNamePlaceholder")} value={form.lastName} onChange={set("lastName")} />
          </div>
        </div>

        <div className="pm__form-row">
          <div className="pm__form-group">
            <label className="pm__form-label">{t("modal.dateOfBirth", "Date of Birth")} <span className="pm__required">*</span></label>
            <input className="pm__form-input" type="date" value={form.dateOfBirth} onChange={set("dateOfBirth")} />
          </div>
          <div className="pm__form-group">
            <label className="pm__form-label">{t("modal.age", "Age")}</label>
            <input className="pm__form-input" type="number" value={calculatedAge || ""} disabled placeholder={t("modal.agePlaceholder")} />
          </div>
          <div className="pm__form-group">
            <label className="pm__form-label">{t("modal.gender")} <span className="pm__required">*</span></label>
            <div className="pm__radio-group">
              {GENDERS.map((g) => (
                <label key={g.value} className={`pm__radio-label${form.gender === g.value ? " pm__radio-label--active" : ""}`}>
                  <input type="radio" name="gender" value={g.value} checked={form.gender === g.value} onChange={set("gender")} />
                  {g.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="pm__form-group">
          <label className="pm__form-label">{t("modal.phone")} <span className="pm__required">*</span></label>
          <input className="pm__form-input" placeholder={t("modal.phonePlaceholder")} value={form.phone} onChange={set("phone")} />
        </div>
        <div className="pm__form-group">
          <label className="pm__form-label">{t("modal.email")} <span className="pm__optional">{t("modal.optional")}</span></label>
          <input className="pm__form-input" type="email" placeholder={t("modal.emailPlaceholder")} value={form.email} onChange={set("email")} />
        </div>

        <div className="pm__modal-footer">
          <button className="pm__modal-submit" onClick={handleSubmit} disabled={!canSubmit || submitting}>
            <UserPlus size={15} /> {submitting ? "..." : editing ? "Save" : t("modal.submit")}
          </button>
          <button className="pm__modal-cancel" onClick={onClose}>{t("modal.cancel")}</button>
        </div>
      </div>
    </div>
  );
}

/* ========================= Patient Card ========================= */
function PatientCard({ row, onProfile, onFile, onAskAuthorization, onEditLocalPatient, onToggleLocalPatient, onDeleteLocalPatient, isRequestingAuthorization, failedAvatars, setFailedAvatars, allowPatientNavigation = true }) {
  const { t } = useTranslation("patientsList");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const initials = getInitials({ firstName: row.firstName, lastName: row.lastName, fullName: row.name, email: row.email });
  const color    = avatarColor(row.name || row.firstName || "");
  const source   = row.source || "local";

  return (
    <div className="pm__card" onClick={() => setOpen(true)}>
      <div className="pm__card-top">
        <div className="pm__avatar" style={{ background: color }}>
          {row.avatarUrl && !failedAvatars[row.patientId] ? (
            <img src={row.avatarUrl} alt={row.name} onError={() => setFailedAvatars((p) => ({ ...p, [row.patientId]: true }))} />
          ) : (
            initials
          )}
        </div>

        <div className="pm__card-identity">
          <p className="pm__card-name">{row.name || "—"}</p>
          <p className="pm__card-meta">
            {row.gender || "—"}
          </p>
        </div>

        {row.identifier && <div className="pm__id-badge">{displayIdentifier(row.identifier)}</div>}

        <span className={`pm__source-badge pm__source-badge--${source}`}>
          {t(`source.${source}`, { defaultValue: source })}
        </span>

        <div className="pm__kebab-wrap" ref={ref}>
          <button className="pm__kebab-btn" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} aria-label="Options">
            <MoreVertical size={18} />
          </button>
          {open && (
            <div className="pm__dropdown">
              {!row.accessAllowed && isMedilinkSource(row.source) && (
                <div className="pm__dropdown-hint">{t("card.awaitingAuthorization", { defaultValue: "Awaiting patient authorization" })}</div>
              )}
              {row.canAskAuthorization && (
                <button
                  className="pm__dropdown-item pm__dropdown-item--ask"
                  disabled={isRequestingAuthorization}
                  onClick={() => {
                    onAskAuthorization?.(row);
                    setOpen(false);
                  }}
                >
                  <UserPlus size={15} />
                  {isRequestingAuthorization
                    ? t("card.requestingAuthorization", { defaultValue: "Requesting..." })
                    : row.accessStatus === "pending"
                      ? t("card.askAgainAuthorization", { defaultValue: "Ask Again" })
                      : t("card.askAuthorization", { defaultValue: "Ask Authorization" })}
                </button>
              )}
              {allowPatientNavigation ? (
                <>
                  <button
                    className={`pm__dropdown-item pm__dropdown-item--profile${!row.accessAllowed ? " pm__dropdown-item--disabled" : ""}`}
                    disabled={!row.accessAllowed}
                    onClick={() => { setOpen(false); onProfile(row); }}
                  >
                    <Eye size={15} /> {t("card.profile")}
                  </button>
                  <button
                    className={`pm__dropdown-item pm__dropdown-item--file${!row.accessAllowed ? " pm__dropdown-item--disabled" : ""}`}
                    disabled={!row.accessAllowed}
                    onClick={() => { setOpen(false); onFile(row); }}
                  >
                    <FileText size={15} /> {t("card.file")}
                  </button>
                </>
              ) : null}
              {row.source === "local" ? (
                <>
                  <button className="pm__dropdown-item" onClick={() => { setOpen(false); onEditLocalPatient?.(row); }}>
                    <Edit2 size={15} /> {t("actions.edit")}
                  </button>
                  <button className="pm__dropdown-item" onClick={() => { setOpen(false); onToggleLocalPatient?.(row); }}>
                    {row.isActive ? <Power size={15} /> : <RotateCcw size={15} />}
                    {row.isActive ? t("actions.deactivate") : t("actions.reactivate")}
                  </button>
                  <button className="pm__dropdown-item" onClick={() => { setOpen(false); onDeleteLocalPatient?.(row); }}>
                    <Trash2 size={15} /> {t("actions.delete")}
                  </button>
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <div className="pm__card-info">
        <div className="pm__info-row"><Calendar size={13} /><span>{formatAgeDisplay(row.age, t)}</span></div>
        {row.phone && (
          <div className="pm__info-row"><Phone size={13} /><span>{formatPatientPhone(row.phone)}</span></div>
        )}
        {row.email && (
          <div className="pm__info-row"><Mail size={13} /><span>{row.email}</span></div>
        )}
        <div className="pm__info-row">
          <Calendar size={13} />
          <span>{row.lastVisit ? t("card.lastVisit", { date: row.lastVisit }) : t("card.lastVisit", { date: "—" })}</span>
        </div>
        <div className="pm__info-row">
          <Clipboard size={13} />
          <span>{row.visits != null ? t("card.visits", { count: row.visits }) : "—"}</span>
        </div>
      </div>

      {row.conditions?.length > 0 && (
        <div className="pm__tags">
          {row.conditions.map((c) => <span key={c} className="pm__tag">{c}</span>)}
        </div>
      )}
    </div>
  );
}

/* ========================= Patient Table Header ========================= */
function PatientListHeader() {
  const { t } = useTranslation("patientsList");
  return (
    <div className="pm__table-header">
      <span className="pm__th">{t("table.patient")}</span>
      <span className="pm__th">{t("table.type")}</span>
      <span className="pm__th">{t("table.contact")}</span>
      <span className="pm__th">{t("table.lastVisit")}</span>
      <span className="pm__th">{t("table.totalVisits")}</span>
      <span className="pm__th">{t("table.actions")}</span>
    </div>
  );
}

/* ========================= Patient List Row ========================= */
function PatientListRow({ row, onProfile, onFile, onAskAuthorization, onEditLocalPatient, onToggleLocalPatient, onDeleteLocalPatient, isRequestingAuthorization, failedAvatars, setFailedAvatars, allowPatientNavigation = true }) {
  const { t } = useTranslation("patientsList");
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.right - 180,
      });
    }
  }, [open]);

  const initials = getInitials({ firstName: row.firstName, lastName: row.lastName, fullName: row.name, email: row.email });
  const color    = avatarColor(row.name || row.firstName || "");
  const source   = row.source || "local";
  const ageLine  = [row.age ? formatAgeDisplay(row.age, t) : null, row.gender].filter(Boolean).join(" • ") || "—";

  return (
    <div className="pm__table-row" onClick={() => setOpen(true)}>
      {/* PATIENT */}
      <div className="pm__td pm__td--patient">
        <div className="pm__avatar" style={{ background: color, width: 40, height: 40, minWidth: 40, fontSize: 13 }}>
          {row.avatarUrl && !failedAvatars[row.patientId] ? (
            <img src={row.avatarUrl} alt={row.name} onError={() => setFailedAvatars((p) => ({ ...p, [row.patientId]: true }))} />
          ) : initials}
        </div>
        <div>
          <p className="pm__table-name">{row.name || "—"}</p>
          <p className="pm__table-sub">{ageLine}</p>
          {row.identifier && <div className="pm__id-badge" style={{ marginTop: "4px" }}>{displayIdentifier(row.identifier)}</div>}
        </div>
      </div>

      {/* TYPE */}
      <div className="pm__td">
        <span className={`pm__source-badge pm__source-badge--${source}`}>
          {t(`source.${source}`, { defaultValue: source })}
        </span>
      </div>

      {/* CONTACT */}
      <div className="pm__td pm__td--contact">
        <div className="pm__contact-item"><Calendar size={13} /><span>{formatAgeDisplay(row.age, t)}</span></div>
        {row.phone && <div className="pm__contact-item"><Phone size={13} /><span>{formatPatientPhone(row.phone)}</span></div>}
        {row.email && <div className="pm__contact-item pm__contact-item--email"><Mail size={13} /><span>{row.email}</span></div>}
      </div>

      {/* LAST VISIT */}
      <div className="pm__td">
        <span className="pm__table-date">{row.lastVisit || "—"}</span>
      </div>

      {/* TOTAL VISITS */}
      <div className="pm__td">
        <span className="pm__table-visits">{row.visits ?? "—"}</span>
      </div>

      {/* ACTIONS */}
      <div className="pm__td pm__td--actions" ref={ref}>
        <button className="pm__kebab-btn" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} aria-label="Options">
          <MoreVertical size={18} />
        </button>
        {open && (
          <div className="pm__dropdown pm__dropdown--fixed" ref={dropdownRef} style={{ top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px` }}>
            {!row.accessAllowed && isMedilinkSource(row.source) && (
              <div className="pm__dropdown-hint">{t("card.awaitingAuthorization", { defaultValue: "Awaiting patient authorization" })}</div>
            )}
            {row.canAskAuthorization && (
              <button
                className="pm__dropdown-item pm__dropdown-item--ask"
                disabled={isRequestingAuthorization}
                onClick={() => {
                  onAskAuthorization?.(row);
                  setOpen(false);
                }}
              >
                <UserPlus size={15} />
                {isRequestingAuthorization
                  ? t("card.requestingAuthorization", { defaultValue: "Requesting..." })
                  : row.accessStatus === "pending"
                    ? t("card.askAgainAuthorization", { defaultValue: "Ask Again" })
                    : t("card.askAuthorization", { defaultValue: "Ask Authorization" })}
              </button>
            )}
            {allowPatientNavigation ? (
              <>
                <button
                  className={`pm__dropdown-item pm__dropdown-item--profile${!row.accessAllowed ? " pm__dropdown-item--disabled" : ""}`}
                  disabled={!row.accessAllowed}
                  onClick={() => { setOpen(false); onProfile(row); }}
                >
                  <Eye size={15} /> {t("card.profile")}
                </button>
                <button
                  className={`pm__dropdown-item pm__dropdown-item--file${!row.accessAllowed ? " pm__dropdown-item--disabled" : ""}`}
                  disabled={!row.accessAllowed}
                  onClick={() => { setOpen(false); onFile(row); }}
                >
                  <FileText size={15} /> {t("card.file")}
                </button>
              </>
            ) : null}
            {row.source === "local" ? (
              <>
                <button className="pm__dropdown-item" onClick={() => { setOpen(false); onEditLocalPatient?.(row); }}>
                  <Edit2 size={15} /> {t("actions.edit")}
                </button>
                <button className="pm__dropdown-item" onClick={() => { setOpen(false); onToggleLocalPatient?.(row); }}>
                  {row.isActive ? <Power size={15} /> : <RotateCcw size={15} />}
                  {row.isActive ? t("actions.deactivate") : t("actions.reactivate")}
                </button>
                <button className="pm__dropdown-item" onClick={() => { setOpen(false); onDeleteLocalPatient?.(row); }}>
                  <Trash2 size={15} /> {t("actions.delete")}
                </button>
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

/* ========================= Helper Functions ========================= */
const formatDateOfBirth = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    // Retourner au format standardisé YYYY-MM-DD (ex: 1990-05-15)
    return date.toISOString().split('T')[0];
  } catch (e) {
    return "";
  }
};

/* ========================= Main Component ========================= */
const PatientsList = ({ allowPatientNavigation = true }) => {
  const [currentPage,   setCurrentPage]   = useState(1);
  const [failedAvatars, setFailedAvatars] = useState({});
  const [viewMode,      setViewMode]      = useState("list");
  const [filter,        setFilter]        = useState("all");
  const [search,        setSearch]        = useState("");
  const [showModal,     setShowModal]     = useState(false);
  const [patientPendingDelete, setPatientPendingDelete] = useState(null);
  const [isDeletingPatient, setIsDeletingPatient] = useState(false);
  const [accessByPatient, setAccessByPatient] = useState({});
  const [requestingAuthorizationByPatient, setRequestingAuthorizationByPatient] = useState({});
  const [authorizationTarget, setAuthorizationTarget] = useState(null);
  const [requestedScopes, setRequestedScopes] = useState(DEFAULT_ACCESS_SCOPES);

  const ITEMS_PER_PAGE = 6;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t }    = useTranslation("patientsList");
  const { usersById, loading: usersLoading, error: usersError } = useSelector((s) => s.user);
  const doctorUser = useSelector((s) => s.doctor?.user || s.auth?.user || null);
  const doctorId =
    doctorUser?.id ||
    doctorUser?._id ||
    doctorUser?.doctorId ||
    doctorUser?.medecinId ||
    null;
  const patientsArray = useMemo(
    () => Object.values(usersById || {}),
    [usersById]
  );
  const rdvItems = useSelector((s) => s.rendezvous?.byMedecin?.items || []);
  const reduxLocalPatients = useSelector(selectAllLocalPatients) || [];
  const localPatientsLoading = useSelector(selectLocalPatientsLoading);
  const localPatientKeySet = useMemo(
    () => buildLocalPatientKeySet(reduxLocalPatients),
    [reduxLocalPatients]
  );

  useEffect(() => { 
    dispatch(fetchUsers()); 
    dispatch(fetchLocalPatients({ activeOnly: false }));
  }, [dispatch]);

  useEffect(() => {
    if (doctorId) dispatch(fetchRendezvousByMedecin({ userId: doctorId }));
  }, [dispatch, doctorId]);

  useEffect(() => {
    let mounted = true;
    if (!doctorId) {
      setAccessByPatient({});
      return () => {
        mounted = false;
      };
    }

    (async () => {
      try {
        const { data } = await axiosRDV.get(`/access-requests/doctor/${encodeURIComponent(String(doctorId))}`);
        if (!mounted) return;

        const list = arr(data);
        const next = {};
        list.forEach((item) => {
          const pid = String(item?.patientId ?? "").trim();
          if (!pid) return;
          const subprofileId = String(item?.subprofileId ?? "").trim() || null;
          const targetKey = medicalAccessTargetKey(pid, subprofileId);
          const candidate = {
            status: String(item?.status || "pending").toLowerCase(),
            requestId: item?.id ?? null,
            scopes: Array.isArray(item?.scopes) ? item.scopes : [],
            requestedScopes: Array.isArray(item?.requestedScopes)
              ? item.requestedScopes
              : [],
            allowProfile: Boolean(item?.allowProfile),
            allowMedicalFile: Boolean(item?.allowMedicalFile),
            subprofileId,
          };
          // Pendant une demande d'extension, l'ancien grant de lecture reste actif.
          if (!next[targetKey] || (candidate.status === "accepted" && next[targetKey].status !== "accepted")) {
            next[targetKey] = candidate;
          }
        });
        setAccessByPatient(next);
      } catch {
        if (mounted) setAccessByPatient({});
      }
    })();

    return () => {
      mounted = false;
    };
  }, [doctorId]);

  /* ── Derive lastVisit + visits count from doctor's appointments ── */
  const visitStats = useMemo(() => {
    const map = {};
    const now = Date.now();
    rdvItems.forEach((r) => {
      const profileId = r.subprofileId ?? r.effectiveProfileId ?? r.patientId;
      const pid = profileId != null ? String(profileId) : null;
      if (!pid) return;
      const t = r.startTime ? new Date(r.startTime).getTime() : null;
      if (!t || t > now) return; // only past appointments
      if (!map[pid]) map[pid] = { visits: 0, lastTs: 0 };
      map[pid].visits += 1;
      if (t > map[pid].lastTs) map[pid].lastTs = t;
    });
    // format lastTs → "DD/MM/YYYY"
    Object.values(map).forEach((v) => {
      if (v.lastTs) {
        const d = new Date(v.lastTs);
        v.lastVisit = `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
      } else {
        v.lastVisit = null;
      }
    });
    return map;
  }, [rdvItems]);

  const reduxRows = useMemo(() =>
    patientsArray
      .map((p) => {
      const navigationPatientId = String(pickNavigationPatientId(p));
      const hasSubprofile = Boolean(
        p.subprofileId ||
        p.subProfileId ||
        p.profileScope === "subprofile" ||
        normalizeSource(p.source) === "subprofile"
      );
      const source = hasSubprofile ? "subprofile" : normalizeSource(p.source) || "medilink";
      const principalPatientId = String(
        p.patientId ?? p.raw?.linkedUserId ?? navigationPatientId,
      ).trim();
      const subprofileId = hasSubprofile
        ? String(p.subprofileId ?? p.subProfileId ?? p.effectiveProfileId ?? navigationPatientId).trim()
        : null;
      const effectiveProfileId = subprofileId || navigationPatientId;
      const stats = visitStats[effectiveProfileId] || {};
      const access = accessByPatient[medicalAccessTargetKey(principalPatientId, subprofileId)] || null;
      const accessStatus = access?.status || "none";
      const grantedScopes = Array.isArray(access?.scopes) ? access.scopes : [];
      const dateOfBirth = p.dateOfBirth || p.birthdate || p.birthDate || null;
      const patientName = [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || p.fullName || p.name || "";
      const identityKeys = getRowIdentityKeys({
        id: p.id,
        _id: p._id,
        patientId: p.patientId,
        identifier: p.identifier,
        patientIdentifier: p.patientIdentifier,
        uuid: p.uuid,
        externalId: p.externalId,
        phone: p.phone || p.phoneNumber,
        phoneNumber: p.phoneNumber,
        email: p.email,
        firstName: p.firstName,
        lastName: p.lastName,
        fullName: p.fullName,
        name: patientName,
      });
      const hasLocalEquivalent = source !== "local" && identityKeys.some((key) => localPatientKeySet.has(key));
      
      // Calculate age from dateOfBirth if available
      const age = normalizeAge(p.age, dateOfBirth);
      
      return {
        patientId:  toNumericIfPossible(effectiveProfileId),
        principalPatientId,
        subprofileId,
        effectiveProfileId,
        identifier: displayIdentifier(
          p.identifier
        ),
        firstName:  p.firstName || "",
        lastName:   p.lastName  || "",
        fullName:   p.fullName || [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || null,
        name:       p.fullName || [p.firstName, p.lastName].filter(Boolean).join(" ").trim() || null,
        email:      p.email     || "",
        phone:      p.phone     || p.phoneNumber || "",
        age:        age,
        gender:     p.gender    || null,
        dateOfBirth,
        birthdate:  dateOfBirth,
        birthDate:  dateOfBirth,
        source,
        avatarUrl:  p.avatarUrl || p.avatar || p.photo || null,
        lastVisit:  p.lastVisit || stats.lastVisit || null,
        visits:     p.visits    ?? stats.visits    ?? null,
        conditions: Array.isArray(p.conditions) ? p.conditions : [],
        accessStatus,
        accessScopes: grantedScopes,
        accessAllowed:
          (accessStatus === "accepted" && (access?.allowProfile || access?.allowMedicalFile)) ||
          source === "local",
        canAskAuthorization:
          isMedilinkSource(source) &&
          (accessStatus !== "accepted" ||
            MEDICAL_ACCESS_SCOPE_OPTIONS.some(({ value }) => !grantedScopes.includes(value))),
      };
      })
      .filter(Boolean),
  [patientsArray, visitStats, accessByPatient, localPatientKeySet]);

  const localRows = useMemo(() =>
    reduxLocalPatients.map((p) => {
      const dateOfBirth = p.dateOfBirth || p.birthdate || p.birthDate || null;
      const age = normalizeAge(p.age, dateOfBirth);
      
      
      return {
        patientId:  p.id,
        identifier: displayIdentifier(
          p.identifier ||
          p.patientIdentifier ||
          p.cin ||
          p.n_assurance ||
          p.uuid ||
          p.externalId ||
          p.id
        ),
        firstName:  p.firstName || "",
        lastName:   p.lastName  || "",
        fullName:   p.fullName  || p.name || [p.firstName, p.lastName].filter(Boolean).join(" "),
        name:       p.fullName  || p.name || [p.firstName, p.lastName].filter(Boolean).join(" "),
        email:      p.email     || "",
        phone:      p.phone     || "",
        age:        age,
        gender:     p.gender    || null,
        dateOfBirth: dateOfBirth,
        birthdate:  dateOfBirth,
        birthDate:  dateOfBirth,
        source:     "local",
        isActive:   p.isActive !== false,
        avatarUrl:  null,
        lastVisit:  null,
        visits:     null,
        conditions: [],
        accessStatus: "accepted",
        accessAllowed: true,
        canAskAuthorization: false,
      };
    }),
  [reduxLocalPatients]);

  const allRows = useMemo(() => [...reduxRows, ...localRows], [reduxRows, localRows]);

  const filtered = useMemo(() => {
    let rows = allRows;
    if (filter === "medilink") {
      rows = rows.filter((r) => isMedilinkSource(r.source));
    } else if (filter !== "all") {
      rows = rows.filter((r) => r.source === filter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        (r.name  || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.phone || "").includes(q) ||
        formatDateOfBirth(r.dateOfBirth).includes(q)
      );
    }
    return rows;
  }, [allRows, filter, search]);

  const totalCount    = allRows.length;
  const medilinkCount = allRows.filter((r) => isMedilinkSource(r.source)).length;
  const localCount    = allRows.filter((r) => r.source === "local").length;

  useEffect(() => setCurrentPage(1), [filtered.length, filter, search]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const displayed  = useMemo(() => filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE), [filtered, startIndex]);

  const handleViewProfile = (row) => {
    if (!allowPatientNavigation || !row?.accessAllowed) return;
    const query = new URLSearchParams();
    if (row.principalPatientId) query.set("patientId", row.principalPatientId);
    if (row.subprofileId) query.set("subprofileId", row.subprofileId);
    navigate(`${DOCTOR_ROUTES.DOCTOR_PATIENT_VIEW(row.patientId)}${query.toString() ? `?${query}` : ""}`);
  };
  const handleViewFile = (row) => {
    if (!allowPatientNavigation || !row?.accessAllowed) return;
    const query = new URLSearchParams();
    if (row.principalPatientId) query.set("patientId", row.principalPatientId);
    if (row.subprofileId) query.set("subprofileId", row.subprofileId);
    navigate(`${DOCTOR_ROUTES.FILE_PAGE(row.patientId)}${query.toString() ? `?${query}` : ""}`);
  };

  const [editingLocalPatient, setEditingLocalPatient] = useState(null);

  const refreshLocalPatients = () => {
    dispatch(fetchLocalPatients({ activeOnly: false }));
  };

  const handleEditLocalPatient = (row) => setEditingLocalPatient(row);

  const handleToggleLocalPatient = async (row) => {
    const confirmationKey = row.isActive
      ? "actions.deactivateConfirm"
      : "actions.reactivateConfirm";
    if (!window.confirm(t(confirmationKey))) return;
    await dispatch(
      row.isActive
        ? deactivateLocalPatient({ patientId: row.patientId })
        : reactivateLocalPatient({ patientId: row.patientId })
    ).unwrap();
    refreshLocalPatients();
  };

  const handleDeleteLocalPatient = async (row) => {
    setPatientPendingDelete(row);
  };

  const confirmDeleteLocalPatient = async () => {
    if (!patientPendingDelete || isDeletingPatient) return;

    setIsDeletingPatient(true);
    try {
      await dispatch(
        deleteLocalPatient({ patientId: patientPendingDelete.patientId })
      ).unwrap();
      refreshLocalPatients();
      setPatientPendingDelete(null);
    } catch (error) {
      const errorMessage =
        error?.message || error?.data?.message || "Unable to delete this patient.";
      message.error(errorMessage);
    } finally {
      setIsDeletingPatient(false);
    }
  };

  const openAuthorizationDialog = (row) => {
    const currentScopes = Array.isArray(row?.accessScopes) ? row.accessScopes : [];
    setRequestedScopes(currentScopes.length ? currentScopes : DEFAULT_ACCESS_SCOPES);
    setAuthorizationTarget(row);
  };

  const handleAskAuthorization = async (row, scopes) => {
    const patientId = String(row?.principalPatientId ?? row?.patientId ?? "").trim();
    const subprofileId = String(row?.subprofileId ?? "").trim() || null;
    const targetKey = medicalAccessTargetKey(patientId, subprofileId);
    const normalizedDoctorId = String(doctorId ?? "").trim();
    if (!patientId || !normalizedDoctorId) return;

    setRequestingAuthorizationByPatient((prev) => ({ ...prev, [targetKey]: true }));
    try {
      const { data } = await axiosRDV.post(
        "/access-requests",
        {
          patientId,
          ...(subprofileId ? { subprofileId } : {}),
          scopes,
        },
        {
          // patientId désigne ici la ressource cible. L'identité du médecin
          // reste volontairement absente du body et est dérivée de son JWT.
          allowedRequestFields: ["patientId", "subprofileId"],
        },
      );

      const payload = data?.data ?? data ?? {};
      setAccessByPatient((prev) => {
        const current = prev?.[targetKey];
        if (current?.status === "accepted" && payload?.status === "pending") {
          return prev;
        }
        return {
          ...prev,
          [targetKey]: {
            status: String(payload?.status || "pending").toLowerCase(),
            requestId: payload?.id ?? current?.requestId ?? null,
            scopes: Array.isArray(payload?.scopes) ? payload.scopes : [],
            requestedScopes: Array.isArray(payload?.requestedScopes)
              ? payload.requestedScopes
              : scopes,
            allowProfile: Boolean(payload?.allowProfile),
            allowMedicalFile: Boolean(payload?.allowMedicalFile),
            subprofileId,
          },
        };
      });
      setAuthorizationTarget(null);
    } catch {
    } finally {
      setRequestingAuthorizationByPatient((prev) => ({ ...prev, [targetKey]: false }));
    }
  };

  const FILTERS = ["all", "medilink", "local"];

  if (usersLoading || localPatientsLoading) return <p className="pm__state">{t("loading")}</p>;
  if (usersError)   return <p className="pm__state">⚠ {t("error", { error: usersError })}</p>;

  return (
    <div className="pm">
      {/* ── Header ── */}
      <div className="pm__header">
        <div>
          <h1 className="pm__title">{t("title")}</h1>
          <p className="pm__subtitle">{t("subtitle", { total: totalCount })}</p>
        </div>
        <button className="pm__add-btn" onClick={() => setShowModal(true)}>
          <UserPlus size={16} /> {t("addPatient")}
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="pm__stats">
        <div className="pm__stat-card">
          <div className="pm__stat-left">
            <div className="pm__stat-icon"><Users size={22} /></div>
            <p className="pm__stat-label">{t("stats.total")}</p>
          </div>
          <span className="pm__stat-value">{totalCount}</span>
        </div>
        <div className="pm__stat-card">
          <div className="pm__stat-left">
            <div className="pm__stat-icon"><ExternalLink size={22} /></div>
            <p className="pm__stat-label">{t("stats.medilink")}</p>
          </div>
          <span className="pm__stat-value">{medilinkCount}</span>
        </div>
        <div className="pm__stat-card">
          <div className="pm__stat-left">
            <div className="pm__stat-icon"><Clipboard size={22} /></div>
            <p className="pm__stat-label">{t("stats.local")}</p>
          </div>
          <span className="pm__stat-value">{localCount}</span>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="pm__toolbar">
        <div className="pm__search-wrap">
          <Search size={15} className="pm__search-icon" />
          <input
            className="pm__search"
            placeholder={t("toolbar.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="pm__filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              className={`pm__filter-btn${filter === f ? " pm__filter-btn--active" : ""}`}
              onClick={() => setFilter(f)}
            >
              {t(`filters.${f}`)}
            </button>
          ))}
        </div>

        <div className="pm__view-toggle">
          <button className={`pm__view-btn${viewMode === "list" ? " pm__view-btn--active" : ""}`} onClick={() => setViewMode("list")} aria-label="List view">
            <List size={16} />
          </button>
          <button className={`pm__view-btn${viewMode === "grid" ? " pm__view-btn--active" : ""}`} onClick={() => setViewMode("grid")} aria-label="Grid view">
            <LayoutGrid size={16} />
          </button>
        </div>
      </div>

      {/* ── Grid / List ── */}
      {viewMode === "grid" ? (
        <div className="pm__grid">
          {displayed.map((row) => (
            <PatientCard
              key={rowKey(row)}
              row={row}
              onProfile={handleViewProfile}
              onFile={handleViewFile}
              onAskAuthorization={openAuthorizationDialog}
              onEditLocalPatient={handleEditLocalPatient}
              onToggleLocalPatient={handleToggleLocalPatient}
              onDeleteLocalPatient={handleDeleteLocalPatient}
              // isRequestingAuthorization={!!requestingAuthorizationByPatient[String(row.patientId)]}
              isRequestingAuthorization={!!requestingAuthorizationByPatient[rowAccessTargetKey(row)]}
              failedAvatars={failedAvatars}
              setFailedAvatars={setFailedAvatars}
              allowPatientNavigation={allowPatientNavigation}
            />
          ))}
        </div>
      ) : (
        <div className="pm__table">
          <PatientListHeader />
          <div className="pm__table-body">
            {displayed.map((row) => (
              <PatientListRow
                key={rowKey(row)}
                row={row}
                onProfile={handleViewProfile}
                onFile={handleViewFile}
                onAskAuthorization={openAuthorizationDialog}
                onEditLocalPatient={handleEditLocalPatient}
                onToggleLocalPatient={handleToggleLocalPatient}
                onDeleteLocalPatient={handleDeleteLocalPatient}
                // isRequestingAuthorization={!!requestingAuthorizationByPatient[String(row.patientId)]}
                isRequestingAuthorization={!!requestingAuthorizationByPatient[rowAccessTargetKey(row)]}
                failedAvatars={failedAvatars}
                setFailedAvatars={setFailedAvatars}
                allowPatientNavigation={allowPatientNavigation}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="pm__pagination">
          <button className="pm__page-btn" onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1}>
            <ChevronLeft size={16} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={`pm__page-dot${currentPage === i + 1 ? " pm__page-dot--active" : ""}`}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button className="pm__page-btn" onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages}>
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* ── Add Patient Modal ── */}
      {(showModal || editingLocalPatient) && (
        <AddPatientModal
          patient={editingLocalPatient}
          onClose={() => {
            setShowModal(false);
            setEditingLocalPatient(null);
          }}
          onCreated={() => {
            /* Patient added to Redux, will be picked up by selectAllLocalPatients */
          }}
          onUpdated={() => setEditingLocalPatient(null)}
        />
      )}

      <ConfirmDeleteModal
        open={Boolean(patientPendingDelete)}
        title={t("modal.deleteTitle")}
        message={t("modal.deleteMessage", {
          name: patientPendingDelete?.name || "this patient",
        })}
        confirmText={isDeletingPatient ? t("modal.deleting") : t("modal.deleteConfirm")}
        cancelText={t("modal.cancel")}
        loading={isDeletingPatient}
        onCancel={() => setPatientPendingDelete(null)}
        onConfirm={confirmDeleteLocalPatient}
      />

      <Modal
        open={Boolean(authorizationTarget)}
        title="Demander un accès médical"
        okText="Envoyer la demande"
        cancelText="Annuler"
        okButtonProps={{
          disabled: requestedScopes.length === 0,
          loading: Boolean(
            authorizationTarget &&
              requestingAuthorizationByPatient[rowAccessTargetKey(authorizationTarget)]
          ),
        }}
        onCancel={() => setAuthorizationTarget(null)}
        onOk={() => handleAskAuthorization(authorizationTarget, requestedScopes)}
      >
        <Alert
          type="info"
          showIcon
          message="Le patient verra et validera explicitement chacun de ces droits."
          style={{ marginBottom: 16 }}
        />
        <Checkbox.Group
          value={requestedScopes}
          onChange={(values) => setRequestedScopes(values)}
          style={{ display: "grid", gap: 10 }}
        >
          {MEDICAL_ACCESS_SCOPE_OPTIONS.map((option) => (
            <Checkbox key={option.value} value={option.value}>
              {option.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Modal>
    </div>
  );
};

export default PatientsList;
